// ============================================================================
// TeamPayrollTargets (M-682, GC-E) — parts de masse par equipe vs cibles admin
// ============================================================================
// Port FIDELE du PoC components/salary/team-payroll-targets.tsx (112 L @ SHA
// 7e40e4a). Purement presentationnel : les TeamTargetRow sont calculees CLIENT
// (buildTeamTargetRows, lib pure) a partir des RealPayrollRow serialisees serveur.
//
// TRANSPOSITION (§GC-E, Regle #28) : Section shadcn -> carte locale (GS_COMPONENTS
// .card + en-tete) ; GaugeBar `barClassName bg-primary/*` -> GaugeBar gest
// (employee/ui, prop `className` = classe de la barre) en tokens brand ; Intl
// fr-CA en dur (formatCurrency/Percent) -> helpers locale (format.ts, P-CE18) ;
// tokens shadcn (bg-muted, text-primary, text-success-foreground) -> slate/brand/
// emerald dark-aware. Libelles FR -> i18n FR+EN. Regle #4 : zero emoji.

import { GS_COMPONENTS } from '@gs/ui';
import { useTranslation } from '../../i18n';
import { GaugeBar } from '../employee/ui';
import { formatCad, formatSignedPct, formatPctPlain } from './format';
import type { TeamTargetRow } from '../../lib/salary/payroll-targets';

// Teinte de l'ecart (points de %) : positif = l'equipe pese plus que sa cible
// (brand/defavorable), negatif = sous la cible (emeraude), nul/absent = neutre.
function deltaClass(delta: number | null): string {
  if (delta === null || delta === 0) return 'text-slate-500 dark:text-slate-400';
  return delta > 0
    ? 'text-brand-600 dark:text-brand-400'
    : 'text-emerald-600 dark:text-emerald-400';
}

export interface TeamPayrollTargetsProps {
  rows: TeamTargetRow[];
  year: number;
  /** Libelle de sous-periode (ex. « 1 janv. – 31 mars 2026 ») — absent => annee. */
  periodLabel?: string;
}

export function TeamPayrollTargets({ rows, year, periodLabel }: TeamPayrollTargetsProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  if (rows.length === 0) return null;

  const hasTargets = rows.some((r) => r.targetPct !== null);
  // Echelle commune des jauges : le max des parts ET des cibles, pour que les
  // barres restent comparables entre equipes et qu'un depassement se voie.
  const gaugeMax = Math.max(...rows.map((r) => Math.max(r.sharePct, r.targetPct ?? 0)), 0.01);

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-3 p-5`}>
      <div className="flex flex-col gap-0.5">
        <h2 className="text-body font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:grilleCe.reel.teamTargets.title')}
        </h2>
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:grilleCe.reel.teamTargets.description', { scope: periodLabel ?? String(year) })}
        </p>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full border-collapse text-field">
          <thead>
            <tr className="bg-slate-100/70 text-meta uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">
              <th className="px-3 py-2 text-left font-medium">{t('gest:grilleCe.reel.teamTargets.team')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('gest:grilleCe.reel.teamTargets.headcount')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('gest:grilleCe.reel.teamTargets.mass')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('gest:grilleCe.reel.teamTargets.share')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('gest:grilleCe.reel.teamTargets.target')}</th>
              <th
                className="px-3 py-2 text-right font-medium"
                title={t('gest:grilleCe.reel.teamTargets.deltaTooltip')}
              >
                {t('gest:grilleCe.reel.teamTargets.delta')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.teamId ?? '__none__'} className="border-t border-slate-200 dark:border-slate-700">
                <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">{r.teamName}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.headcount}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatCad(r.cost, locale)}</td>
                <td className="px-3 py-2">
                  <div className="flex min-w-40 items-center gap-2">
                    <GaugeBar
                      value={r.sharePct * 100}
                      max={gaugeMax * 100}
                      className={r.deltaPct !== null && r.deltaPct > 0 ? 'bg-brand-500' : 'bg-brand-300'}
                    />
                    <span className="w-14 shrink-0 text-right tabular-nums">{formatPctPlain(r.sharePct, locale)}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {r.targetPct !== null ? formatPctPlain(r.targetPct, locale) : '—'}
                </td>
                <td className={`px-3 py-2 text-right font-medium tabular-nums ${deltaClass(r.deltaPct)}`}>
                  {r.deltaPct !== null ? formatSignedPct(r.deltaPct, locale) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!hasTargets ? (
        <p className="text-meta text-slate-500 dark:text-slate-400">
          {t('gest:grilleCe.reel.teamTargets.noTargets', { year })}
        </p>
      ) : null}
    </section>
  );
}
